import prepareTranslate from '@/helpers/dictionary';
import routes from '@/routes';
import useAccessToken from '@/hooks/useAccessToken';
import PageTemplate from '@/components/PageTemplate';
import { MonthPickerInput } from '@mantine/dates';
import { objectToQueryString } from '@/helpers/string';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation';
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { routeWithParams } from '@/helpers/route';
import agentMutations from '@/api/mutitations/agent';
import { notifications } from '@mantine/notifications';
import { createArraysFromRange } from '@/helpers/dates';
import {
  Button,
  Grid,
  Loader,
  Paper,
  Select,
  Stack,
} from '@mantine/core';
import { startOfMonth, addMonths } from 'date-fns';
import exportRuleQuery from '@/api/queries/exportRule';

const MonthlyPayment = () => {
  useAccessToken();

  const { mutateAsync: agentMutate } = agentMutations.useGetAgents();
  const { data: exportRuleData } = exportRuleQuery.useGetAll();
  const reportId = exportRuleData?.find((item) => item.key === 'commissions');

  const navigate = useNavigate();

  const t = prepareTranslate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const allAgentsOption = { label: t('pages.reports.labels.agents'), value: 'all' };
  const isAllAgents = selectedAgent === 'all';

  const form = useForm({
    mode: 'controlled',
    validate: {
      agent: () => validations.agent(selectedAgent, isAllAgents),
      period: (value) => validations.required(value),
    },
  });

  const handleFormSubmit = () => {
    const { hasErrors, errors } = form.validate();

    if (hasErrors) {
      const firstErrorPath = Object.keys(errors)[0];
      form.getInputNode(firstErrorPath)?.focus();
      return;
    }

    const values = form.getValues();

    values.company = [];
    values.product = [];

    if (!Array.isArray(values.period)) {
      values.period = [values.period];
    }
    const [yearArray, monthArray] = createArraysFromRange(...values.period);
    const queryData = {
      month: monthArray,
      year: yearArray,
      company: values.company,
      product: values.product,
      category: values.category,
      showChart: reportId.showChart,
      useDateSelectors: reportId.useDateSelectors,
    };

    if (isAllAgents) {
      queryData.agent = [];
    } else {
      queryData.agent = [selectedAgent];
    }

    const query = objectToQueryString(queryData);

    const { path } = routes.viewMonthlyPayment;

    navigate(routeWithParams(path, { id: reportId._id, query }));
  };

  const loadAgents = async (search = '') => {
    setLoading(true);
    try {
      const query = search ? { 'personalDetails.fullName': search } : {};
      const { data: agentData } = await agentMutate({ query, start: 1 });
      setData([
        allAgentsOption,
        ...agentData.map((agent) => ({
          label: `${agent.personalDetails.fullName} (${agent.personalDetails.uid})`,
          value: agent._id,
        })),
      ]);
    } catch {
      notifications.show({
        title: t('pages.reports.notification.error.title'),
        message: t('pages.reports.notification.error.message'),
        color: 'red',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useDebouncedCallback((val) => {
    loadAgents(val);
  }, 400);

  const actions = [
    <Button key="primary"
      onClick={handleFormSubmit}
      mt={'2rem'}
      mr={'1rem'}
      w={'7rem'}
    >{t('pages.reports.action')}
    </Button>,
  ];

  const startOfNextMonth = startOfMonth(addMonths(new Date(), 1));

  return (
    <>
        <PageTemplate
            title={routes.monthlyPayment.name}
        >
        <PageTemplate.Panel id="form">
          <form>
            <Paper p="xl" mt={'2rem'} mr={'1rem'}>
                 <Grid gutter="lg" p={'1rem'} pb={'3rem'}>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                  <Stack gap={'lg'}>

                   <MonthPickerInput
                    label={t('pages.reports.form.daterange')}
                    required
                    key={form.key('period')}
                    {...form.getInputProps('period')}
                     maxDate={startOfNextMonth}
                    />
                        </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
  <Stack gap={'lg'}>

    <Select
      label={t('pages.reports.form.agent')}
      data={data}
      required
      searchable
      clearable
      key={form.key('agent')}
      {...form.getInputProps('agent')}
      value={selectedAgent}
      onChange={setSelectedAgent}
      onSearchChange={handleSearchChange}
      nothingFoundMessage={loading ? <Loader size='xs' /> : t('pages.reports.noAgents')}
    />
  </Stack>
</Grid.Col>

              </Grid>
            </Paper>
              </form>
          {actions}
        </PageTemplate.Panel>
      </PageTemplate>
    </>
  );
};

export default MonthlyPayment;
